import { useEffect, useState } from 'react';
import type { Store } from '@nexus/store';
import { useStore } from './context';
import { saveFile } from '../lib/export';

export interface AttachmentUrl {
  url: string;
  mime: string;
  name: string;
}

// Object URLs live for the session: attachments are immutable and reused often.
const urls = new Map<string, AttachmentUrl>();

async function resolve(store: Store, target: string): Promise<AttachmentUrl | null> {
  const info = await store.findAttachment(target);
  if (!info) return null;
  const cached = urls.get(info.id);
  if (cached) return cached;
  const blob = await store.attachmentData(info.id);
  if (!blob) return null;
  const value = {
    url: URL.createObjectURL(new Blob([blob.data as BlobPart], { type: blob.mime })),
    mime: blob.mime,
    name: info.name,
  };
  urls.set(info.id, value);
  return value;
}

export function useAttachment(target: string | null): AttachmentUrl | null | undefined {
  const store = useStore();
  const [loaded, setLoaded] = useState<{ target: string; value: AttachmentUrl | null } | null>(
    null,
  );
  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    const load = () =>
      void resolve(store, target).then((value) => {
        if (!cancelled) setLoaded({ target, value });
      });
    load();
    const off = store.subscribe(load, ['attachments']);
    return () => {
      cancelled = true;
      off();
    };
  }, [store, target]);
  if (!target) return null;
  return loaded?.target === target ? loaded.value : undefined;
}

/** Open an attachment: a new tab in the browser, a save dialog on the desktop. */
export async function openAttachment(store: Store, target: string): Promise<void> {
  const info = await store.findAttachment(target);
  const blob = info && (await store.attachmentData(info.id));
  if (!info || !blob) return;
  if ('__TAURI_INTERNALS__' in window) {
    await saveFile(info.name, blob.data, blob.mime);
  } else {
    const found = await resolve(store, target);
    if (found) window.open(found.url, '_blank', 'noopener');
  }
}

/** Largest file a page accepts (they sync to every device). */
export const MAX_ATTACHMENT = 25 * 1024 * 1024;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Store a pasted, dropped or picked file as an attachment; returns the name to
 * link (`![[name]]`). Pasted images get Obsidian's "Pasted image <time>.png" name;
 * a name already in use gets a number.
 */
export async function uploadAttachment(store: Store, file: File): Promise<string | null> {
  if (file.size > MAX_ATTACHMENT) return null;
  const d = new Date();
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const ext = file.name.includes('.')
    ? file.name.split('.').pop()!
    : file.type.split('/')[1] || 'bin';
  const pasted = !file.name || /^image\.\w+$/i.test(file.name);
  const base = pasted ? `Pasted image ${stamp}` : file.name.replace(/\.[^.]+$/, '');
  let name = `${base}.${ext}`;
  for (let i = 1; await store.findAttachment(name); i++) name = `${base} ${i}.${ext}`;
  await store.addAttachment({
    name,
    mime: file.type || 'application/octet-stream',
    data: new Uint8Array(await file.arrayBuffer()),
  });
  return name;
}
